/**
 * Component 10: Benefit Analysis
 * Measurable improvements, investment requirements, ROI calculations
 */

import React, { useState, useEffect } from 'react';
import {
  Plus as PlusIcon,
  Edit3 as EditIcon,
  Trash2 as TrashIcon,
  TrendingUp as TrendingUpIcon,
  DollarSign as DollarSignIcon,
  Clock as ClockIcon,
  Square as SquareIcon,
  Zap as ZapIcon,
  Shield as ShieldIcon,
  CheckCircle as QualityIcon,
  Truck as DeliveryIcon,
  Heart as MoraleIcon,
  Settings as SettingsIcon,
  Calculator as CalculatorIcon,
  BarChart3 as ChartIcon
} from 'lucide-react';

import type { KATABoardV2, BenefitMeasurement, BenefitMeasurementCreate } from '../../../types/kataBoardV2';
import { benefitMeasurementApi } from '../../../services/kataBoardV2';
import { useAlert } from '../../../hooks/useAlert';
import { useKataBoardCanEdit } from '../../../contexts/KataBoardAccessContext';

interface Component10Props {
  kataBoard: KATABoardV2;
}

// Benefit type configurations
const BENEFIT_TYPES = {
  // Measurable Improvements
  time_saved: {
    label: 'Time Saved',
    icon: ClockIcon,
    color: 'blue',
    category: 'improvements',
    defaultUnit: 'minutes'
  },
  space_optimized: {
    label: 'Space Optimized',
    icon: SquareIcon,
    color: 'green',
    category: 'improvements',
    defaultUnit: 'sq ft'
  },
  energy_reduction: {
    label: 'Energy Reduction',
    icon: ZapIcon,
    color: 'yellow',
    category: 'improvements',
    defaultUnit: 'kWh'
  },
  other_improvement: {
    label: 'Other Improvement',
    icon: SettingsIcon,
    color: 'purple',
    category: 'improvements',
    defaultUnit: ''
  },
  
  // Investment
  investment_required: {
    label: 'Investment Required',
    icon: DollarSignIcon,
    color: 'red',
    category: 'investment',
    defaultUnit: '€'
  },
  
  // Quality Metrics
  safety_improvement: {
    label: 'Safety',
    icon: ShieldIcon,
    color: 'green',
    category: 'metrics',
    defaultUnit: '%'
  },
  quality_improvement: {
    label: 'Quality',
    icon: QualityIcon,
    color: 'blue',
    category: 'metrics',
    defaultUnit: '%'
  },
  delivery_improvement: {
    label: 'Delivery',
    icon: DeliveryIcon,
    color: 'orange',
    category: 'metrics',
    defaultUnit: '%'
  },
  cost_reduction: {
    label: 'Cost Reduction',
    icon: DollarSignIcon,
    color: 'green',
    category: 'metrics',
    defaultUnit: '€'
  },
  morale_improvement: {
    label: 'Morale',
    icon: MoraleIcon,
    color: 'pink',
    category: 'metrics',
    defaultUnit: '%'
  }
};

const IMPACT_COLORS = {
  low: 'bg-gray-100 text-gray-800 border-gray-200',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  high: 'bg-green-100 text-green-800 border-green-200'
};

const Component10_Benefit: React.FC<Component10Props> = ({ kataBoard }) => {
  // Alert hook
  const { confirmDelete, AlertComponent } = useAlert();
  const canEdit = useKataBoardCanEdit();
  
  // State management
  const [benefits, setBenefits] = useState<BenefitMeasurement[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingBenefit, setEditingBenefit] = useState<BenefitMeasurement | null>(null);
  const [summary, setSummary] = useState<any>(null);
  
  // Form state
  const [formData, setFormData] = useState<BenefitMeasurementCreate>({
    kata_board: kataBoard.id,
    measurement_type: 'time_saved',
    description: '',
    value: null,
    unit: '',
    financial_impact: null,
    impact_level: 'medium'
  });

  // Load benefits
  useEffect(() => {
    const loadBenefits = async () => {
      if (!kataBoard.id) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const [benefitsData, summaryData] = await Promise.all([
          benefitMeasurementApi.getAll({ kata_board: kataBoard.id }),
          benefitMeasurementApi.getSummary(kataBoard.id)
        ]);
        
        setBenefits(benefitsData);
        setSummary(summaryData);
      } catch (error) {
        console.error('Failed to load benefits:', error);
        setError('Failed to load benefit data');
      } finally {
        setLoading(false);
      }
    };

    loadBenefits();
  }, [kataBoard.id]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      if (editingBenefit) {
        console.log('TAKING UPDATE PATH - editingBenefit.id:', editingBenefit.id);
        const updatedBenefit = await benefitMeasurementApi.update(editingBenefit.id, formData);
        setBenefits(prev => prev.map(b => b.id === editingBenefit.id ? updatedBenefit : b));
      } else {
        console.log('TAKING CREATE PATH - Making POST request');
        const newBenefit = await benefitMeasurementApi.create(formData);
        console.log('CREATE RESPONSE:', newBenefit);
        setBenefits(prev => [...prev, newBenefit]);
      }
      
      // Reload summary
      const summaryData = await benefitMeasurementApi.getSummary(kataBoard.id);
      setSummary(summaryData);
      
      // Reset form
      resetForm();
    } catch (error) {
      console.error('Failed to save benefit:', error);
      setError('Failed to save benefit');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async (benefit: BenefitMeasurement) => {
    if (!canEdit) return;
    await confirmDelete(`the ${BENEFIT_TYPES[benefit.measurement_type as keyof typeof BENEFIT_TYPES]?.label} benefit`, async () => {
      try {
        setLoading(true);
        await benefitMeasurementApi.delete(benefit.id);
        setBenefits(prev => prev.filter(b => b.id !== benefit.id));
        
        // Reload summary
        const summaryData = await benefitMeasurementApi.getSummary(kataBoard.id);
        setSummary(summaryData);
      } catch (error) {
        console.error('Failed to delete benefit:', error);
        setError('Failed to delete benefit');
      } finally {
        setLoading(false);
      }
    });
  };

  // Handle edit
  const handleEdit = (benefit: BenefitMeasurement) => {
    if (!canEdit) return;
    setEditingBenefit(benefit);
    setFormData({
      kata_board: kataBoard.id,
      measurement_type: benefit.measurement_type,
      description: benefit.description,
      value: benefit.value,
      unit: benefit.unit,
      financial_impact: benefit.financial_impact,
      impact_level: benefit.impact_level
    });
    setShowAddForm(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      kata_board: kataBoard.id,
      measurement_type: 'time_saved',
      description: '',
      value: null,
      unit: '',
      financial_impact: null,
      impact_level: 'medium'
    });
    setEditingBenefit(null);
    setShowAddForm(false);
  };

  // Handle measurement type change
  const handleTypeChange = (type: string) => {
    const config = BENEFIT_TYPES[type as keyof typeof BENEFIT_TYPES];
    setFormData(prev => ({
      ...prev,
      measurement_type: type,
      unit: config?.defaultUnit || ''
    }));
  };

  // Calculate ROI
  const calculateROI = () => {
    if (!summary) return 0;
    
    const totalBenefits = summary.total_financial_impact || 0;
    const totalInvestment = benefits
      .filter(b => b.measurement_type === 'investment_required')
      .reduce((sum, b) => sum + (b.financial_impact || 0), 0);
    
    if (totalInvestment === 0) return 0;
    return ((totalBenefits - totalInvestment) / totalInvestment) * 100;
  };

  // Group benefits by category
  const groupedBenefits = benefits.reduce((acc, benefit) => {
    const config = BENEFIT_TYPES[benefit.measurement_type as keyof typeof BENEFIT_TYPES];
    const category = config?.category || 'other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(benefit);
    return acc;
  }, {} as Record<string, BenefitMeasurement[]>);

  if (loading && benefits.length === 0) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6 border border-green-200">
          <div className="flex items-center space-x-3">
            <div className="bg-green-600 rounded-lg p-2 animate-pulse">
              <TrendingUpIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-green-900">Benefit Analysis</h3>
              <p className="text-green-700 text-sm">Loading benefit data...</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-1/4"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6 border border-green-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-green-600 rounded-lg p-2">
              <TrendingUpIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-green-900">Benefit Analysis</h3>
              <p className="text-green-700 text-sm">Measurable improvements and ROI calculations</p>
            </div>
          </div>
          
          {canEdit && (
          <button
            onClick={() => {
              setEditingBenefit(null);
              setShowAddForm(true);
            }}
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Benefit
          </button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center">
              <ChartIcon className="h-8 w-8 text-blue-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Total Benefits</p>
                <p className="text-2xl font-semibold text-gray-900">{summary.total_benefits}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center">
              <DollarSignIcon className="h-8 w-8 text-green-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Financial Impact</p>
                <p className="text-2xl font-semibold text-gray-900">
                  €{summary.total_financial_impact?.toLocaleString() || '0'}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center">
              <CalculatorIcon className="h-8 w-8 text-purple-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">ROI</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {calculateROI().toFixed(1)}%
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center">
              <TrendingUpIcon className="h-8 w-8 text-orange-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">High Impact</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {summary.by_impact_level?.high || 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Benefits by Category */}
      <div className="space-y-6">
        {/* Measurable Improvements */}
        {groupedBenefits.improvements && (
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h4 className="text-lg font-medium text-gray-900">Measurable Improvements</h4>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {groupedBenefits.improvements.map((benefit, index) => {
                  const config = BENEFIT_TYPES[benefit.measurement_type as keyof typeof BENEFIT_TYPES];
                  const IconComponent = config?.icon || SettingsIcon;
                  
                  return (
                    <div key={benefit.id || `improvement-${index}`} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-3">
                          <IconComponent className={`h-6 w-6 text-${config?.color}-600`} />
                          <div>
                            <h5 className="font-medium text-gray-900">{config?.label}</h5>
                            <p className="text-sm text-gray-500">{benefit.description}</p>
                          </div>
                        </div>
                        {canEdit && (
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => handleEdit(benefit)}
                            className="p-1 text-gray-400 hover:text-gray-600"
                          >
                            <EditIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(benefit)}
                            className="p-1 text-gray-400 hover:text-red-600"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                        )}
                      </div>
                      
                      <div className="mt-3 space-y-2">
                        {benefit.value && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Value:</span>
                            <span className="font-medium">{benefit.value} {benefit.unit}</span>
                          </div>
                        )}
                        {benefit.financial_impact && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Financial Impact:</span>
                            <span className="font-medium text-green-600">€{benefit.financial_impact.toLocaleString()}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Impact:</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium border ${IMPACT_COLORS[benefit.impact_level]}`}>
                            {benefit.impact_level.charAt(0).toUpperCase() + benefit.impact_level.slice(1)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Investment Required */}
        {groupedBenefits.investment && (
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h4 className="text-lg font-medium text-gray-900">Investment Required</h4>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {groupedBenefits.investment.map((benefit, index) => (
                  <div key={benefit.id || `investment-${index}`} className="flex items-center justify-between p-4 border border-red-200 rounded-lg bg-red-50">
                    <div className="flex items-center space-x-3">
                      <DollarSignIcon className="h-6 w-6 text-red-600" />
                      <div>
                        <h5 className="font-medium text-gray-900">Investment Required</h5>
                        <p className="text-sm text-gray-600">{benefit.description}</p>
                        {benefit.financial_impact && (
                          <p className="text-lg font-semibold text-red-600">€{benefit.financial_impact.toLocaleString()}</p>
                        )}
                      </div>
                    </div>
                    {canEdit && (
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => handleEdit(benefit)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                      >
                        <EditIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(benefit)}
                        className="p-1 text-gray-400 hover:text-red-600"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Quality Metrics */}
        {groupedBenefits.metrics && (
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h4 className="text-lg font-medium text-gray-900">Quality Metrics</h4>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {groupedBenefits.metrics.map((benefit, index) => {
                  const config = BENEFIT_TYPES[benefit.measurement_type as keyof typeof BENEFIT_TYPES];
                  const IconComponent = config?.icon || SettingsIcon;
                  
                  return (
                    <div key={benefit.id || `metrics-${index}`} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-3">
                          <IconComponent className={`h-6 w-6 text-${config?.color}-600`} />
                          <div>
                            <h5 className="font-medium text-gray-900">{config?.label}</h5>
                            <p className="text-sm text-gray-500">{benefit.description}</p>
                          </div>
                        </div>
                        {canEdit && (
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => handleEdit(benefit)}
                            className="p-1 text-gray-400 hover:text-gray-600"
                          >
                            <EditIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(benefit)}
                            className="p-1 text-gray-400 hover:text-red-600"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                        )}
                      </div>
                      
                      <div className="mt-3 space-y-2">
                        {benefit.value && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Value:</span>
                            <span className="font-medium">{benefit.value} {benefit.unit}</span>
                          </div>
                        )}
                        {benefit.financial_impact && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Financial Impact:</span>
                            <span className="font-medium text-green-600">€{benefit.financial_impact.toLocaleString()}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Impact:</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium border ${IMPACT_COLORS[benefit.impact_level]}`}>
                            {benefit.impact_level.charAt(0).toUpperCase() + benefit.impact_level.slice(1)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* No Benefits Message */}
      {benefits.length === 0 && !loading && (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <TrendingUpIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Benefits Added Yet</h3>
          <p className="text-gray-500 mb-4">
            Start tracking your improvements by adding measurable benefits and ROI calculations.
          </p>
          {canEdit && (
          <button
            onClick={() => {
              setEditingBenefit(null);
              setShowAddForm(true);
            }}
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add First Benefit
          </button>
          )}
        </div>
      )}

      {/* Add/Edit Form Modal */}
      {showAddForm && canEdit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {editingBenefit ? 'Edit Benefit' : 'Add New Benefit'}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Benefit Type
                </label>
                <select
                  value={formData.measurement_type}
                  onChange={(e) => handleTypeChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                >
                  {Object.entries(BENEFIT_TYPES).map(([key, config]) => (
                    <option key={key} value={key}>{config.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Describe the benefit or improvement"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Value
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.value || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, value: e.target.value ? parseFloat(e.target.value) : null }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="0.00"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unit
                  </label>
                  <input
                    type="text"
                    value={formData.unit}
                    onChange={(e) => setFormData(prev => ({ ...prev, unit: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="minutes, €, %"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Financial Impact (€)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.financial_impact || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, financial_impact: e.target.value ? parseFloat(e.target.value) : null }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="0.00"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Impact Level
                </label>
                <select
                  value={formData.impact_level}
                  onChange={(e) => setFormData(prev => ({ ...prev, impact_level: e.target.value as 'low' | 'medium' | 'high' }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : (editingBenefit ? 'Update' : 'Add')} Benefit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Alert Component */}
      {AlertComponent}
    </div>
  );
};

export default Component10_Benefit;

